/**
 * Confirm Dialog Component
 *
 * Reusable confirmation dialog with customizable title, message, and button text.
 * Supports different severity levels (info, warning, error) for button styling.
 * Mobile-responsive with stacked button layout on small screens.
 */
import {
  Button,
  Dialog,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  useMediaQuery
} from '@mui/material';

import './ConfirmDialog.css';
import { ConfirmDialogProps } from './ConfirmDialog.types';

/**
 * ConfirmDialog Component
 *
 * Displays a confirmation dialog with customizable content and actions.
 * Responsive design with mobile-optimized button layout.
 
 */
export const ConfirmDialog = ({
  open,
  title,
  message,
  confirmText = 'Підтвердити',
  cancelText = 'Скасувати',
  onConfirm,
  onCancel,
  severity = 'info'
}: ConfirmDialogProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getConfirmColor = () => {
    switch (severity) {
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'primary';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="xs"
      fullWidth
      className="confirm-dialog"
      PaperProps={{
        sx: {
          ...(isMobile && {
            margin: 2,
            width: 'calc(100% - 32px)'
          })
        }
      }}
    >
      <DialogTitle className="confirm-dialog-title">{title}</DialogTitle>
      <DialogContent className="confirm-dialog-content">
        <Typography>{message}</Typography>
      </DialogContent>
      <DialogActions
        className="confirm-dialog-actions"
        sx={{
          ...(isMobile && {
            flexDirection: 'column',
            gap: 1,
            p: 2,
            '& > :not(style) + :not(style)': {
              marginLeft: 0
            }
          })
        }}
      >
        <Button onClick={onCancel} sx={{ ...(isMobile && { width: '100%', order: 2 }) }}>
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color={getConfirmColor()}
          sx={{ ...(isMobile && { width: '100%', order: 1 }) }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
